import React, { useEffect, useRef, useState } from "react";
import { Info, CircleDot, X } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * VehicleBaselineStrip
 * ------------------------------------------------------------------
 * Small transparency band shown between the pack health chart and the
 * KPI tiles on Home. Its job is to explain — in one line — *why* the
 * SoH % is what it is: which Tesla-published EPA number we're using as
 * the "when new" denominator, plus the trim + wheel type when Tesla's
 * `vehicle_config` disambiguates them.
 *
 * This matters most for 2021-2023 Model S Plaid, where 21" Arachnid
 * wheels rate at 348 mi but 19" Tempest wheels rate at 396 mi — a
 * 48 mi difference in the SoH denominator, or ~14 percentage points
 * of SoH. Same story for Model 3 (272 / 315 / 353 / 358 / 363 mi
 * across five trim × year combinations).
 */

// Human-readable wheel-type mapping. Tesla's `wheel_type` codes are
// terse (`Arachnid21Grey`, `Tempest19`, `TrackWheel20`, etc.) — this
// turns them into a short badge users can recognize.
function humanizeWheel(code) {
  if (!code) return null;
  const s = String(code);
  if (/arachnid/i.test(s)) return { name: "Arachnid", size: "21\"" };
  if (/tempest/i.test(s)) return { name: "Tempest", size: "19\"" };
  if (/track/i.test(s)) return { name: "Track", size: "20\"" };
  if (/induction/i.test(s)) return { name: "Induction", size: "20\"" };
  if (/uberturbine/i.test(s)) return { name: "Uberturbine", size: "21\"" };
  if (/turbine/i.test(s)) return { name: "Turbine", size: "22\"" };
  if (/gemini/i.test(s)) return { name: "Gemini", size: "18\"" };
  if (/aero/i.test(s)) return { name: "Aero", size: "18\"" };
  if (/sport/i.test(s)) return { name: "Sport", size: "19\"" };
  // Fallback: strip trailing "XxColor" size markers so we don't render junk
  const clean = s.replace(/(\d{2}).*/, "");
  const sizeMatch = /(\d{2})/.exec(s);
  return { name: clean || "Stock", size: sizeMatch ? `${sizeMatch[1]}\"` : "" };
}

export const VehicleBaselineStrip = ({ vehicle }) => {
  // Hooks MUST run in the same order every render — declare them before
  // any conditional return.
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsErr, setDetailsErr] = useState(false);
  const popRef = useRef(null);

  const model = vehicle?.model || null;
  const year = vehicle?.year || null;
  const trim = vehicle?.trim || null;
  const epa = vehicle?.epaRange || null;
  const packKwh = vehicle?.packKwh || null;
  const wheel = humanizeWheel(vehicle?.config?.wheel_type);

  // Fetch backend EPA reference (notes + tesla_claimed_mi) on first open.
  useEffect(() => {
    if (!open || details || detailsErr) return;
    if (!model || !year || !API_BASE) return;
    const url = new URL(`${API_BASE}/api/tesla/epa_reference/compare`);
    url.searchParams.set("model", model);
    url.searchParams.set("year", year);
    if (trim) url.searchParams.set("trim", String(trim).split(" (")[0]);
    fetch(url.toString())
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("epa_fetch_failed"))))
      .then((j) => {
        if (!j.found || !j.rows?.length) return setDetailsErr(true);
        const row = j.rows[0];
        setDetails({
          notes: row.notes || "",
          tesla_claimed_mi: row.tesla_claimed_mi,
          epa_mi: row.epa_mi,
          delta_mi: row.epa_vs_tesla_delta_mi || 0,
          warranty_years: row.warranty_years,
          warranty_miles: row.warranty_miles,
          warranty_threshold_pct: row.warranty_threshold_pct,
        });
      })
      .catch(() => setDetailsErr(true));
  }, [open, details, detailsErr, model, year, trim]);

  // Close the popover when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Only render for a live-connected vehicle.
  if (!vehicle?.live) return null;
  // If we have no additional context to add, keep the strip minimal.
  if (!epa && !trim) return null;

  return (
    <div className="relative" ref={popRef}>
      <div
        className="rounded-lg border px-3 py-2 flex items-center gap-2.5"
        style={{
          borderColor: "rgba(0,229,255,0.20)",
          background: "rgba(0,229,255,0.04)",
        }}
        data-testid="vehicle-baseline-strip"
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="shrink-0 rounded-full p-0.5 hover:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-cyan-400"
          aria-expanded={open}
          aria-label="Show EPA baseline details"
          data-testid="vehicle-baseline-info-btn"
        >
          <Info className="w-3.5 h-3.5" style={{ color: "#00E5FF" }} strokeWidth={2.2} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
            Baseline · EPA vs live
          </div>
          <div className="text-[11px] text-white leading-snug flex items-baseline flex-wrap gap-x-1.5" data-testid="vehicle-baseline-line">
            {trim && (
              <span className="font-semibold" data-testid="vehicle-baseline-trim">
                {trim}
              </span>
            )}
            {epa && (
              <>
                <span className="text-zinc-500">·</span>
                <span data-testid="vehicle-baseline-epa">
                  <span className="font-mono" style={{ color: "#00E5FF" }}>{epa} mi</span> EPA
                </span>
              </>
            )}
            {epa && (
              <>
                <span className="text-zinc-500">·</span>
                <span data-testid="vehicle-baseline-actual">
                  {vehicle?.liveRangeAt100 != null ? (
                    <span className="font-mono" style={{ color: "#39FF14" }}>
                      {Math.round(vehicle.liveRangeAt100)} mi
                    </span>
                  ) : (
                    <span className="font-mono text-zinc-500">—</span>
                  )}{" "}
                  actual
                </span>
              </>
            )}
            {vehicle?.odometer > 0 && (
              <>
                <span className="text-zinc-500">·</span>
                <span data-testid="vehicle-baseline-odometer">
                  <span className="font-mono text-white">
                    {Math.round(vehicle.odometer).toLocaleString()}
                  </span>{" "}
                  mi driven
                </span>
              </>
            )}
            {packKwh && (
              <>
                <span className="text-zinc-500">·</span>
                <span className="font-mono text-zinc-300" data-testid="vehicle-baseline-pack">
                  {packKwh} kWh
                </span>
              </>
            )}
          </div>
        </div>
        {wheel && (
          <div
            className="shrink-0 flex items-center gap-1.5 rounded-md border px-2 py-1"
            style={{
              borderColor: "rgba(255,255,255,0.10)",
              background: "rgba(255,255,255,0.03)",
            }}
            data-testid="vehicle-baseline-wheels"
          >
            <CircleDot className="w-3 h-3 text-zinc-400" strokeWidth={2} />
            <span className="font-mono text-[10px] text-white leading-none">
              {wheel.name}
              {wheel.size && <span className="text-zinc-500 ml-0.5">{wheel.size}</span>}
            </span>
          </div>
        )}
      </div>

      {open && (
        <div
          role="dialog"
          data-testid="vehicle-baseline-tooltip"
          className="absolute z-30 left-0 right-0 mt-2 rounded-lg border p-3 shadow-lg"
          style={{
            borderColor: "rgba(0,229,255,0.25)",
            background: "#0a0a0a",
            backdropFilter: "blur(12px)",
          }}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em]" style={{ color: "#00E5FF" }}>
              Baseline details
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-zinc-500 hover:text-white rounded p-0.5"
              aria-label="Close details"
              data-testid="vehicle-baseline-tooltip-close"
            >
              <X className="w-3 h-3" strokeWidth={2.5} />
            </button>
          </div>

          <div
            className="mb-2 rounded-md border border-white/5 bg-white/[0.03] px-2.5 py-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono"
            data-testid="vehicle-baseline-live-readings"
          >
            <span className="text-zinc-500">Charge level</span>
            <span className="text-right text-zinc-200">
              {vehicle?.usableBatteryPct ?? vehicle?.batteryPct ?? "—"}%
            </span>
            <span className="text-zinc-500">Rated range now</span>
            <span className="text-right text-zinc-200">
              {vehicle?.currentRange != null ? `${Math.round(vehicle.currentRange)} mi` : "—"}
            </span>
            <span className="text-zinc-500">Range at 100%</span>
            <span className="text-right" style={{ color: "#39FF14" }}>
              {vehicle?.liveRangeAt100 != null ? `${Math.round(vehicle.liveRangeAt100)} mi` : "— (awaiting charge data)"}
            </span>
            <span className="text-zinc-500">Odometer</span>
            <span className="text-right text-zinc-200">
              {vehicle?.odometer > 0 ? `${Math.round(vehicle.odometer).toLocaleString()} mi` : "—"}
            </span>
          </div>

          {details ? (
            <div className="space-y-2 text-[11px] leading-snug">
              {details.notes && (
                <p className="text-zinc-200" data-testid="vehicle-baseline-notes">
                  {details.notes}
                </p>
              )}
              {details.tesla_claimed_mi != null && details.epa_mi != null && (
                <div className="flex items-baseline justify-between gap-3 pt-1.5 border-t border-white/5">
                  <span className="text-zinc-500 font-mono text-[10px]">EPA vs Tesla-claim</span>
                  <span className="font-mono text-white">
                    {details.epa_mi} <span className="text-zinc-500">/</span>{" "}
                    {details.tesla_claimed_mi} mi
                    {details.delta_mi !== 0 && (
                      <span
                        className="ml-1.5"
                        style={{ color: details.delta_mi > 0 ? "#39FF14" : "#FF6B57" }}
                      >
                        {details.delta_mi > 0 ? "+" : ""}
                        {details.delta_mi}
                      </span>
                    )}
                  </span>
                </div>
              )}
              {details.warranty_years && (
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-zinc-500 font-mono text-[10px]">Warranty floor</span>
                  <span className="font-mono text-white">
                    {details.warranty_threshold_pct}% ·{" "}
                    {details.warranty_years}y / {(details.warranty_miles / 1000).toFixed(0)}k mi
                  </span>
                </div>
              )}
              {!details.notes && !details.tesla_claimed_mi && (
                <div className="text-zinc-500 italic">No additional notes for this trim.</div>
              )}
            </div>
          ) : detailsErr ? (
            <div className="text-[11px] text-zinc-500 italic">No historical notes available for this trim.</div>
          ) : (
            <div className="text-[11px] text-zinc-500 font-mono">Loading…</div>
          )}
        </div>
      )}
    </div>
  );
};
